import { createSlice, Slice, PayloadAction } from '@reduxjs/toolkit';

import { OperationStatus } from '../../types';
import ApiError from '../../types/error';
import {
  PostDeviceSharingRequestPayload,
  PostDeviceSharingRequestResponse,
  GetDeviceSharingRequestsResponse,
  DeviceSharingRequestAction,
  PostDeviceSharingActionPayload,
  PostDeviceSharingActionResponse
} from '../../types/devices';
import { isRequestNotExpired } from '../../utils/is-request-not-expired';

export interface SharedAccessInitialState {
  postDeviceSharingRequest: {
    status: OperationStatus;
    error: ApiError | null;
    data: PostDeviceSharingRequestResponse | null;
  };
  getDeviceSharingRequests: {
    status: OperationStatus;
    error: ApiError | null;
    data: GetDeviceSharingRequestsResponse | null;
  };
  filterDeviceSharingRequests: {
    status: OperationStatus;
    error: ApiError | null;
    data: GetDeviceSharingRequestsResponse | null;
  };
  postDeviceSharingAction: {
    status: OperationStatus;
    error: ApiError | null;
    action: DeviceSharingRequestAction | null;
    email: string | null;
  };
}

const initialState: SharedAccessInitialState = {
  postDeviceSharingRequest: {
    status: OperationStatus.NOT_STARTED,
    error: null,
    data: null
  },
  getDeviceSharingRequests: {
    status: OperationStatus.NOT_STARTED,
    error: null,
    data: null
  },
  filterDeviceSharingRequests: {
    status: OperationStatus.NOT_STARTED,
    error: null,
    data: null
  },
  postDeviceSharingAction: {
    status: OperationStatus.NOT_STARTED,
    error: null,
    action: null,
    email: null
  }
};

const sharedAccessSlice = createSlice({
  name: 'sharedAccess',
  initialState,
  reducers: {
    postDeviceSharingRequest: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<PostDeviceSharingRequestPayload>
    ) => {
      state.postDeviceSharingRequest = {
        status: OperationStatus.RUNNING,
        error: null,
        data: null
      };
    },
    postDeviceSharingSuccess: (
      state,
      action: PayloadAction<PostDeviceSharingRequestResponse>
    ) => {
      state.postDeviceSharingRequest = {
        status: OperationStatus.COMPLETED,
        error: null,
        data: action.payload
      };
      const {
        statusCode: _statusCode,
        created_at: _created_at,
        ...newRequest
      } = action.payload;
      state.getDeviceSharingRequests.data = [
        ...(state.getDeviceSharingRequests.data ?? []),
        newRequest
      ];
    },
    postDeviceSharingFailure: (state, action: PayloadAction<ApiError>) => {
      state.postDeviceSharingRequest = {
        status: OperationStatus.ERRORED,
        error: action.payload,
        data: null
      };
    },
    postDeviceSharingCleanup: (state) => {
      state.postDeviceSharingRequest = initialState.postDeviceSharingRequest;
    },
    getDeviceSharingRequestsRequest: (state) => {
      state.getDeviceSharingRequests = {
        status: OperationStatus.RUNNING,
        error: null,
        data: null
      };
    },
    getDeviceSharingRequestsSuccess: (
      state,
      action: PayloadAction<GetDeviceSharingRequestsResponse>
    ) => {
      state.getDeviceSharingRequests = {
        status: OperationStatus.COMPLETED,
        error: null,
        data: action.payload.filter(isRequestNotExpired)
      };
    },
    getDeviceSharingRequestsFailure: (
      state,
      action: PayloadAction<ApiError>
    ) => {
      state.getDeviceSharingRequests = {
        status: OperationStatus.ERRORED,
        error: action.payload,
        data: null
      };
    },
    getDeviceSharingRequestsCleanup: (state) => {
      state.getDeviceSharingRequests = initialState.getDeviceSharingRequests;
    },
    filterDeviceSharingRequestsRequest: (state) => {
      state.filterDeviceSharingRequests = {
        status: OperationStatus.RUNNING,
        error: null,
        data: null
      };
    },
    filterDeviceSharingRequestsSuccess: (
      state,
      action: PayloadAction<GetDeviceSharingRequestsResponse>
    ) => {
      state.filterDeviceSharingRequests = {
        status: OperationStatus.COMPLETED,
        error: null,
        data: action.payload
      };
    },
    filterDeviceSharingRequestsFailure: (
      state,
      action: PayloadAction<ApiError>
    ) => {
      state.filterDeviceSharingRequests = {
        status: OperationStatus.ERRORED,
        error: action.payload,
        data: null
      };
    },
    filterDeviceSharingRequestsCleanup: (state) => {
      state.filterDeviceSharingRequests =
        initialState.filterDeviceSharingRequests;
    },
    postDeviceSharingAction: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _action: PayloadAction<PostDeviceSharingActionPayload>
    ) => {
      state.postDeviceSharingAction = {
        status: OperationStatus.RUNNING,
        error: null,
        action: _action.payload.action,
        email: _action.payload.email
      };
    },
    postDeviceSharingActionSuccess: (
      state,
      action: PayloadAction<PostDeviceSharingActionResponse>
    ) => {
      state.postDeviceSharingAction = {
        ...state.postDeviceSharingAction,
        status: OperationStatus.COMPLETED,
        error: null
      };
      if (state.getDeviceSharingRequests.data) {
        if (
          state.postDeviceSharingAction.action ===
          DeviceSharingRequestAction.REVOKE
        ) {
          state.getDeviceSharingRequests.data =
            state.getDeviceSharingRequests.data.filter(
              (r) => r.id !== action.payload.id
            );
        } else {
          const requestIndex = state.getDeviceSharingRequests.data.findIndex(
            (r) => r.id === action.payload.id
          );
          if (requestIndex !== -1) {
            const existingPolicies =
              state.getDeviceSharingRequests.data[requestIndex].policies ?? [];
            const policyMap = new Map(
              existingPolicies.map((p) => [p.device_grant_policy, p])
            );
            action.payload.policies.forEach((p) =>
              policyMap.set(p.device_grant_policy, p)
            );
            state.getDeviceSharingRequests.data[requestIndex] = {
              ...state.getDeviceSharingRequests.data[requestIndex],
              policies: Array.from(policyMap.values())
            };
          }

          state.getDeviceSharingRequests.data =
            state.getDeviceSharingRequests.data.filter(isRequestNotExpired);
        }
      }
    },
    postDeviceSharingActionFailure: (
      state,
      action: PayloadAction<ApiError>
    ) => {
      state.postDeviceSharingAction = {
        ...state.postDeviceSharingAction,
        status: OperationStatus.ERRORED,
        error: action.payload
      };
    },
    postDeviceSharingActionCleanup: (state) => {
      state.postDeviceSharingAction = initialState.postDeviceSharingAction;
    }
  }
});

export const {
  postDeviceSharingRequest,
  postDeviceSharingSuccess,
  postDeviceSharingFailure,
  postDeviceSharingCleanup,
  getDeviceSharingRequestsRequest,
  getDeviceSharingRequestsSuccess,
  getDeviceSharingRequestsFailure,
  getDeviceSharingRequestsCleanup,
  filterDeviceSharingRequestsRequest,
  filterDeviceSharingRequestsSuccess,
  filterDeviceSharingRequestsFailure,
  filterDeviceSharingRequestsCleanup,
  postDeviceSharingAction,
  postDeviceSharingActionSuccess,
  postDeviceSharingActionFailure,
  postDeviceSharingActionCleanup
} = sharedAccessSlice.actions;

const sharedAccessSliceExport: Slice<SharedAccessInitialState> =
  sharedAccessSlice;
export default sharedAccessSliceExport;
